import { ListItemIcon, ListItemText } from '@/components/Dropdown/MenuItems/components';
import { upgradeFreePlanModalOpen } from '@/components/Modals/UpgradeFreePlanModal';
import { toast } from '@/components/toast';
import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import EditDraft from '@/images/EditDraft.svg';
import { useAnalytics } from '@/services/analytics';
import { duplicateProject } from '@/services/api';
import { useFlag } from '@/services/featureFlags';
import { itemIsProject, LibraryItem, LibraryItemStatus } from '@d-id/types';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';
import { useSetRecoilState } from 'recoil';

interface Props {
    video: LibraryItem;
    closeMenu: () => void;
}

export const EditAsNewMenuItem = ({ video, closeMenu }: Props) => {
    const { navigateToAdvancedEditor } = useNavigateToAdvancedEditor();
    const mixpanel = useAnalytics();
    const [loading, setLoading] = useState(false);
    const { outOfFreeVideos } = useFreeVideos();
    const setUpgradeFreePlanModalOpen = useSetRecoilState(upgradeFreePlanModalOpen);

    const editAsNewForFailedVideosEnabled = useFlag('edit_as_new_for_failed_videos_enabled', false);
    const allowedStatusesForDuplicate = editAsNewForFailedVideosEnabled
        ? [LibraryItemStatus.Done, LibraryItemStatus.Error]
        : [LibraryItemStatus.Done];
    const isMigratedProject = video?.id?.startsWith('prj_tlk_') || video?.id?.startsWith('prj_clp_');
    const isDuplicatable = video && itemIsProject(video) && !isMigratedProject && allowedStatusesForDuplicate.includes(video.status);

    if (!isDuplicatable) {
        return null;
    }

    const handleClick = async () => {
        if (loading) {
            return;
        }

        if (outOfFreeVideos) {
            mixpanel?.track('duplicate', { event: 'click', from: 'quick-action', videoId: video.id });
            setUpgradeFreePlanModalOpen(true);
            return;
        }

        setLoading(true);

        try {
            const newProjectId = await duplicateProject(video.id);

            mixpanel?.track('duplicate', { event: 'click', from: 'quick-action', videoId: video.id, newVideoId: newProjectId });

            await navigateToAdvancedEditor({
                projectId: newProjectId,
                options: {
                    isEditAsNew: true,
                },
            });
        } catch {
            toast.error({ title: 'Failed to duplicate project' });
        }

        closeMenu();
        setLoading(false);
    };

    return (
        <MenuItem onClick={handleClick}>
            <ListItemIcon>
                <img src={EditDraft} />
            </ListItemIcon>
            <ListItemText>Edit as new</ListItemText>
        </MenuItem>
    );
};
